import { useCallback, useState } from 'react';
import type { StationRef } from '../../../productsApi';
import type { GeoPoint } from './routeEstimate';
import { recomputeTravel } from './components/chainStopUtils';
import { newStationChainStopKey, type StationChainStopValue } from './stationChainFormSchema';
import type { StationChainFormApi } from './useStationChainForm';

/** Aktivní mapový režim — řídí, na co reaguje klik do mapy (banner nahoře + `onMapClick`). */
export type ChainMapMode = { kind: 'idle' } | { kind: 'new-station' };

/**
 * Editor zastávek a mapových režimů detailu trasy. Drží lokální stav (mapový režim, signál
 * vystředění, drag & drop, rozpracovaná „nová stanice z mapy") a operace nad polem `stops`
 * formuláře — přidávání, mazání, přeřazení (pořadí = `Order`) a přepočet odhadů přejezdů.
 */
export function useStationChainEditor(form: StationChainFormApi) {
  const [mapMode, setMapMode] = useState<ChainMapMode>({ kind: 'idle' });
  const [newStationCoord, setNewStationCoord] = useState<GeoPoint | null>(null);
  const [fitSignal, setFitSignal] = useState(0);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  const fit = useCallback(() => setFitSignal((value) => value + 1), []);

  const appendStations = useCallback(
    (refs: readonly StationRef[]) => {
      if (refs.length === 0) {
        return;
      }
      form.setFieldValue('stops', (prev) => {
        const added: StationChainStopValue[] = refs.map((ref) => ({
          key: newStationChainStopKey(),
          id: null,
          stationId: ref.stationId,
          stationName: ref.name,
          stationCode: ref.code,
          stationIsDomestic: ref.isDomestic,
          stationLat: ref.latitude,
          stationLng: ref.longitude,
          stationDetailId: null,
          points: [],
          dwell: 5,
          travel: 0,
          travelAuto: true,
        }));
        return recomputeTravel([...prev, ...added]);
      });
      fit();
    },
    [form, fit]
  );

  const startNewStationFromMap = useCallback(() => {
    setNewStationCoord(null);
    setMapMode((mode) => (mode.kind === 'new-station' ? { kind: 'idle' } : { kind: 'new-station' }));
  }, []);

  const cancelNewStation = useCallback(() => {
    setNewStationCoord(null);
    setMapMode({ kind: 'idle' });
  }, []);

  const handleStationCreated = useCallback(
    (ref: StationRef) => {
      appendStations([ref]);
      cancelNewStation();
    },
    [appendStations, cancelNewStation]
  );

  const handleMapClick = useCallback((lat: number, lng: number) => {
    setNewStationCoord({ lat, lng });
  }, []);

  const removeStop = useCallback(
    (index: number) => {
      form.setFieldValue('stops', (prev) => recomputeTravel(prev.filter((_, i) => i !== index)));
    },
    [form]
  );

  const reorder = useCallback(
    (from: number, to: number) => {
      if (from === to) {
        return;
      }
      form.setFieldValue('stops', (prev) => {
        const arr = [...prev];
        const [item] = arr.splice(from, 1);
        arr.splice(to, 0, item);
        return recomputeTravel(arr);
      });
    },
    [form]
  );

  const setStopDetail = useCallback(
    (index: number, stationDetailId: number | null) => {
      form.setFieldValue('stops', (prev) =>
        recomputeTravel(prev.map((stop, i) => (i === index ? { ...stop, stationDetailId } : stop)))
      );
    },
    [form]
  );

  const setDwell = useCallback(
    (index: number, raw: string) => {
      const value = Math.max(0, Math.round(Number(raw.trim()) || 0));
      form.setFieldValue(`stops[${index}].dwell`, value);
    },
    [form]
  );

  const setTravelManual = useCallback(
    (index: number, raw: string) => {
      const value = Math.max(0, Math.round(Number(raw.trim()) || 0));
      form.setFieldValue('stops', (prev) =>
        prev.map((stop, i) => (i === index ? { ...stop, travel: value, travelAuto: false } : stop))
      );
    },
    [form]
  );

  const resetTravelAuto = useCallback(
    (index: number) => {
      form.setFieldValue('stops', (prev) =>
        recomputeTravel(prev.map((stop, i) => (i === index ? { ...stop, travelAuto: true } : stop)))
      );
    },
    [form]
  );

  return {
    mapMode,
    setMapMode,
    newStationCoord,
    startNewStationFromMap,
    cancelNewStation,
    handleStationCreated,
    handleMapClick,
    fitSignal,
    fit,
    dragIndex,
    setDragIndex,
    dragOverIndex,
    setDragOverIndex,
    appendStations,
    removeStop,
    reorder,
    setStopDetail,
    setDwell,
    setTravelManual,
    resetTravelAuto,
  };
}

/** Typ editoru zastávek trasy (pro propsy podřízených komponent detailu trasy). */
export type StationChainEditor = ReturnType<typeof useStationChainEditor>;
